import {
  CalendarX,
  DotsThreeVertical,
  Gear,
  type Icon,
  Link as LinkIcon,
  Trash,
  UserCheck,
  UserMinus,
} from "@phosphor-icons/react";
import { useNavigate } from "react-router-dom";

import RenderIf from "common/components/RenderIf";
import RestrictedComponent from "common/components/RestrictedComponent";
import { Button } from "common/components/ui/Button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuList,
  DropdownMenuTrigger,
} from "common/components/ui/DropdownMenu";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "common/components/ui/Tooltip";
import useTwBreakpoint from "common/hooks/useTwBreakpoint";
import type { WorkspaceAccount } from "common/types";

import useAccountActions from "../hooks/useAccountActions";

interface AccountActionsDropdownProps {
  account: WorkspaceAccount;
}

export default function AccountActionsDropdown({
  account,
}: AccountActionsDropdownProps) {
  const { license, id: accountId, state, type } = account;

  const { selectAction, components } = useAccountActions(account);
  const navigate = useNavigate();

  const isDesktop = useTwBreakpoint("lg");

  const dropdownMenuItems: {
    label: string;
    icon: Icon;
    onClick: () => void;
  }[] = [];

  if (isDesktop) {
    dropdownMenuItems.unshift({
      onClick: () =>
        navigate({
          pathname: accountId,
          search: location.search, // Preserve query params
        }),
      icon: Gear,
      label: "Account settings",
    });
  }

  if (type === "user") {
    dropdownMenuItems.splice(1, 0, {
      onClick: () => selectAction(license ? "removeLicense" : "assignLicense"),
      label: `${license ? "Unassign" : "Assign"} seat`,
      icon: license ? UserMinus : UserCheck,
    });

    if (license?.expires_at) {
      const label = "Remove cancellation";
      const icon = CalendarX;
      const onClick = () => selectAction("reactivate");

      dropdownMenuItems.splice(1, 0, { label, icon, onClick });
    }

    // NOTE: Reconnect has a priority over removing a cancellation.
    if (state !== "LoggedIn") {
      const icon = LinkIcon;

      dropdownMenuItems.splice(1, 0, {
        label: "Generate reconnect link",
        icon,
        onClick: () => selectAction("reconnectLink"),
      });

      if (!isDesktop) {
        dropdownMenuItems.splice(1, 0, {
          label: "Reconnect",
          icon,
          onClick: () => selectAction("reconnect"),
        });
      }
    }
  }

  return (
    <RestrictedComponent visibleForPermissions={["accounts_full"]}>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button
            variant="secondary-black"
            size={isDesktop ? "sm" : "md"}
            intent="iconOnly"
            className="data-[state='open']:bg-black-200"
            onClick={(e) => e.stopPropagation()}
          >
            <DotsThreeVertical />
          </Button>
        </DropdownMenuTrigger>

        <DropdownMenuContent align="end" onClick={(e) => e.stopPropagation()}>
          <DropdownMenuList>
            {dropdownMenuItems.map(({ icon: Icon, label, onClick }) => (
              <DropdownMenuItem key={label} onClick={onClick}>
                <Icon />
                {label}
              </DropdownMenuItem>
            ))}

            <RenderIf condition={type === "user"}>
              {license ? (
                <Tooltip>
                  <TooltipTrigger asChild disabled>
                    <DropdownMenuItem variant="danger">
                      <Trash />
                      Delete account
                    </DropdownMenuItem>
                  </TooltipTrigger>

                  <TooltipContent side="bottom">
                    You cannot delete an account with an assigned seat
                  </TooltipContent>
                </Tooltip>
              ) : (
                <DropdownMenuItem
                  onClick={() => selectAction("delete")}
                  variant="danger"
                >
                  <Trash />
                  Delete account
                </DropdownMenuItem>
              )}
            </RenderIf>
          </DropdownMenuList>
        </DropdownMenuContent>
      </DropdownMenu>
      {components}
    </RestrictedComponent>
  );
}
